// Whether this deployment serves the measurement tool at all — the one thing
// the consent layer cannot work out for itself.
//
// WHAT GOES WRONG WITHOUT IT. The tracker and the session recorder are served
// from `/s` of this origin by the reverse proxy, and the proxy answers 404 for
// every address under that prefix when it has no upstream to map them onto —
// which is the correct answer for a deployment that runs no measurement
// container, and the state of the dev preview and of every local run. A layer
// that asked for `/s/t.js` anyway would put a failed request and a console
// error on the page of every visitor who pressed Accept, in exchange for
// nothing: there is no tool at the other end to load.
//
// It cannot be discovered from the browser without spending the request this
// exists to avoid, so it is CONFIGURATION, carried on the consent script's own
// tag by whichever template emitted it. Both templates fill it from the same
// environment variable.
//
// Browser-safe: no framework import, no Node built-in.

/** The attribute the two document templates carry the answer on. */
export const MEASUREMENT_FLAG_ATTRIBUTE = "data-measurement";

/**
 * The environment variable both templates read it from.
 *
 * The `PUBLIC_` prefix is the framework's, and it is what lets the app template
 * spell the value as `%sveltekit.env.PUBLIC_MEASUREMENT%`: a page rendered per
 * request takes it from the running process, and a page built ahead of time
 * takes it from the build. Those two are genuinely different moments for the
 * two hundred and nine pages this site prerenders, which is why a deployment
 * that turns measurement on has to set it for the BUILD as well as for the
 * container — the same shape as the content-policy report destination, and
 * documented beside it.
 */
export const MEASUREMENT_ENV_NAME = "PUBLIC_MEASUREMENT";

/**
 * The values that mean "no tool is served here". Everything else means yes.
 *
 * An ABSENT attribute is one of them, and deliberately: a template that has not
 * been taught to carry the flag is a deployment whose measurement addresses
 * nobody has confirmed, and the cost of being wrong in that direction is a
 * missing figure rather than a broken page in front of a visitor.
 */
const OFF = new Set(["", "0", "false", "off", "no"]);

/**
 * Whether the tracker and the recorder may be asked for.
 *
 * Trimmed and lowercased before the comparison, because the value passes
 * through a shell, a compose file, a Docker argument and an HTML attribute
 * before it gets here, and every one of those can leave whitespace behind.
 */
export function measurementConfigured(value: string | null | undefined): boolean {
  if (typeof value !== "string") return false;
  return !OFF.has(value.trim().toLowerCase());
}
